import { useEffect, useSyncExternalStore } from "react";
import api from "@/lib/api";
import { safeUrl } from "@/lib/media";

/**
 * Brochure and cost sheet links are not in the public property data. The backend returns them only in the reply
 * to an enquiry, site-visit or brochure form for that property. This remembers them per property in the browser
 * (with the enquiry id, so the links are refreshed from the server on the next visit and stay current).
 */
const KEY = "eha_unlocked_docs";
const listeners = new Set();
const refreshed = new Set();

const read = () => {
    try {
        const v = JSON.parse(localStorage.getItem(KEY) || "{}");
        return v && typeof v === "object" ? v : {};
    } catch {
        return {};
    }
};
let cache = read();
const emit = () => listeners.forEach((l) => l());
const write = (next) => {
    cache = next;
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* private mode */ }
    emit();
};
window.addEventListener("storage", (e) => { if (e.key === KEY) { cache = read(); emit(); } });

export function rememberUnlock(propertyId, inquiryId, docs) {
    if (!propertyId || !inquiryId || !docs) return;
    write({ ...cache, [propertyId]: { inquiry: inquiryId, docs } });
}

/** Opens a file for download. Uploaded files get the server's "save as" header; other links open normally. */
export const downloadHref = (url) => (/\/api\/uploads\/[^/?]+$/.test(url || "") ? `${url}?download=1` : url);

/**
 * { brochure_url, cost_sheet_url } once this visitor has unlocked the property, otherwise null.
 * An admin who is signed in already receives the links in the property data, so they count as unlocked.
 */
export function useUnlockedDocs(property) {
    const all = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => cache);
    const id = property?.id;
    const entry = id ? all[id] : null;

    useEffect(() => {
        if (!id || !entry?.inquiry || refreshed.has(id)) return;
        refreshed.add(id);   // once per page load: picks up a brochure the admin replaced since
        api.get(`/properties/${id}/documents`, { params: { inquiry: entry.inquiry } })
            .then((r) => write({ ...cache, [id]: { inquiry: entry.inquiry, docs: r.data } }))
            .catch((e) => {
                if (e.response?.status === 404) { const rest = { ...cache }; delete rest[id]; write(rest); }
            });
    }, [id, entry?.inquiry]);

    if (!property) return null;
    if (property.brochure_url !== undefined || property.cost_sheet_url !== undefined) {
        return { brochure_url: safeUrl(property.brochure_url), cost_sheet_url: safeUrl(property.cost_sheet_url) };
    }
    if (!entry) return null;
    return { brochure_url: safeUrl(entry.docs?.brochure_url), cost_sheet_url: safeUrl(entry.docs?.cost_sheet_url) };
}
